import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { isDevMode, Injectable, NgModule } from '@angular/core';
import { FormsModule, FormBuilder, FormArray, FormControl } from '@angular/forms';

function getPrototypeChain(obj, arr = []) {
    const proto = Object.getPrototypeOf(obj);
    if (proto) {
        arr.push(proto);
        return getPrototypeChain(proto, arr);
    }
    return arr.slice(0, -2);
}
/**
 * custom **property decorator**
 */
function annotation(paramValue, symbol) {
    return function (annoatatedClass, propertyName) {
        const target = annoatatedClass.constructor;
        let layout = Object.prototype.hasOwnProperty.call(target, symbol) ? target[symbol] : undefined;
        if (!layout) {
            layout = getPrototypeChain(target)
                .reverse()
                .reduce((l, proto) => {
                const hasLayout = Object.prototype.hasOwnProperty.call(proto, symbol);
                return hasLayout ? Object.assign(l, proto[symbol]) : l;
            }, {});
            target[symbol] = layout;
        }
        layout[propertyName] = paramValue;
    };
}

const layoutSymbol = Symbol('serializationLayout');
class SerializationService {
    constructor(injector) {
        this.injector = injector;
    }
    deserialize(ctor, rawData, input) {
        const layout = ctor[layoutSymbol];
        const newObject = new ctor();
        const promises = [];
        for (const layoutKey in layout) {
            let info = layout[layoutKey];
            info = info ? info.from : info;
            const dataKey = this.getDataKey(layoutKey, info);
            if (!Object.prototype.hasOwnProperty.call(rawData, dataKey) || this.shouldSkip(info)) {
                continue;
            }
            const value = rawData[dataKey];
            // TODO: is this even used? If so, please add a unit test!
            if (typeof info === 'function' || (info && typeof info.map === 'function')) {
                const result = this.mapValue(info, value, newObject, input);
                if (result instanceof Promise) {
                    promises.push(result);
                    result.then((resultval) => (newObject[layoutKey] = resultval)).catch(console.log.bind(console));
                }
                else {
                    newObject[layoutKey] = result;
                }
            }
            else {
                newObject[layoutKey] = value;
            }
        }
        if (isDevMode()) {
            const receivedKeys = Object.keys(rawData);
            receivedKeys.forEach((receivedKey) => {
                let foundKey = null;
                for (const layoutKey in layout) {
                    let info = layout[layoutKey];
                    info = info ? info.from : info;
                    const dataKey = this.getDataKey(layoutKey, info);
                    if (receivedKey === dataKey) {
                        foundKey = layoutKey;
                        break;
                    }
                }
                if (!foundKey) {
                    console.warn(`Deserialization model \`${ctor.name}\` did not expect property \`${receivedKey}: ${rawData[receivedKey]}\``);
                }
            });
        }
        if (promises.length === 0) {
            return Promise.resolve(newObject);
        }
        return Promise.all(promises).then(() => newObject);
    }
    deserializeMany(ctor, rawData) {
        const promises = rawData.map((raw) => this.deserialize(ctor, raw));
        return Promise.all(promises);
    }
    serialize(object, givenCtor, serializationOptions) {
        // givenCtor is required because TypeScript allows `{}` for `T{}` which is okay if it contains
        // the same keypairs. But it generates errors if you want to access the constructor ↓
        const layout = givenCtor[layoutSymbol];
        const promises = [];
        const rawData = {};
        for (const layoutKey in layout) {
            let info = layout[layoutKey];
            info = info ? info.to : info;
            if ((serializationOptions && serializationOptions.removeNull && object[layoutKey] === null) ||
                this.shouldSkip(info)) {
                continue;
            }
            const dataKey = this.getDataKey(layoutKey, info);
            const value = object[layoutKey];
            if (typeof info !== 'boolean' && info && typeof info !== 'string') {
                const result = this.mapValue(info, value, rawData);
                if (result instanceof Promise) {
                    promises.push(result);
                    result.then((resultval) => (rawData[dataKey] = resultval)).catch(console.log.bind(console));
                }
                else {
                    rawData[dataKey] = result;
                }
            }
            else {
                rawData[dataKey] = value;
            }
        }
        return Promise.all(promises).then(() => rawData);
    }
    serializeMany(data, ctor) {
        const promises = data.map((raw) => this.serialize(raw, ctor));
        return Promise.all(promises);
    }
    getDataKey(key, info) {
        if (info !== undefined) {
            if (typeof info === 'string') {
                return info;
            }
            else if (typeof info === 'boolean' || typeof info === 'function') {
                return key;
            }
            else if ('key' in info) {
                return `${info.key}`;
            }
        }
        return key;
    }
    shouldSkip(info) {
        if (typeof info === 'boolean') {
            return !info;
        }
        return false;
    }
    mapValue(info, value, context, input) {
        if (typeof info === 'function') {
            return info(value);
        }
        const args = Array.isArray(info.deps) ? info.deps.map((token) => this.injector.get(token)) : [];
        args.unshift(value);
        args.push(context);
        args.push(input);
        return info.map.apply(undefined, args);
    }
}
SerializationService.ɵfac = function SerializationService_Factory(t) { return new (t || SerializationService)(i0.ɵɵinject(i0.Injector)); };
SerializationService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: SerializationService, factory: SerializationService.ɵfac, providedIn: 'root' });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SerializationService, [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], function () { return [{ type: i0.Injector }]; }, null);
})();
/**
 * Specify whether this property should be serlialized and deserialized when going through the whole object
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
function Serialize(propLayout) {
    return annotation(propLayout, layoutSymbol);
}
class SerializationOptions {
    constructor(obj) {
        this.removeNull = false;
        Object.assign(this, obj);
    }
}

class AautilModule {
}
AautilModule.ɵfac = function AautilModule_Factory(t) { return new (t || AautilModule)(); };
AautilModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: AautilModule });
AautilModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [SerializationService], imports: [[FormsModule, CommonModule]] });
(function () {
    (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AautilModule, [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [FormsModule, CommonModule],
                    providers: [SerializationService],
                }]
        }], null, null);
})();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(AautilModule, { imports: [FormsModule, CommonModule] }); })();

class CheckboxGroupModel {
    constructor(obj) {
        this.name = obj.name;
        this.model = obj.model;
    }
}

const formGroupLayout = Symbol('formGroupLayout');
/**
 * returns all set Validators of the model class
 */
function getValidators(model) {
    return model.constructor[formGroupLayout];
}
/**
 * Specify if a property should be featured in a `FormGroup` generated with `modelToFormGroup()`
 *
 * @param recursive Go recursively into an `object` and return a `FormGroup` with its `FormControl`s
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
function InFormGroup(validators = [], recursive = false) {
    return annotation({ validators, recursive }, formGroupLayout);
}
/**
 * Uses an object to create a FormGroup.
 *
 * Model class needs on properties:
 * `@InFormGroup([Validators.required], true)`
 *
 * @param model The model object with optional global validators for the whole `FormGroup`
 */
function modelToFormGroup(model, validators = []) {
    const formBuilder = new FormBuilder();
    const modelConstructor = model.constructor[formGroupLayout];
    const controlsConfig = {};
    /*
    i.e. set global validators if they exist
    used for example for dependent checks like "password" and "password_confirm"
    */
    let options = {};
    if (model.formOptions !== undefined) {
        options = model.formOptions;
    }
    if (options.validators === undefined || options.validators === null) {
        options.validators = validators;
    }
    else if (Array.isArray(options.validators)) {
        options.validators.concat(validators);
    }
    else {
        options.validators = [options.validators].concat(validators);
    }
    /*
    wrap in array because FormBuilder.group needs something like this:

    ```
    this.newPostForm = this.formBuilder.group({
      title: ['', Validators.required],
      content: ['', Validators.required],
    });
    ```

    Using just `title: '',` works for anything but arrays.
    Also no can be added. Therefore we wrap every value no matter what.
    */
    /* iterate over all keys (all propertys with `@InFormGroup`) in the class */
    for (const key in modelConstructor) {
        if (Object.prototype.hasOwnProperty.call(modelConstructor, key)) {
            const config = modelConstructor[key];
            if (model[key] === undefined || model[key] === null) {
                /* if a property is not initialized, add it anyway with a default value */
                controlsConfig[key] = [null, config.validators];
            }
            else if (typeof model[key] === 'object') {
                if (Array.isArray(model[key])) {
                    if (config.recursive) {
                        controlsConfig[key] = new FormArray([], config.validators);
                        model[key].forEach((element) => {
                            controlsConfig[key].push(modelToFormGroup(element));
                        });
                    }
                    else {
                        controlsConfig[key] = new FormControl(model[key], config.validators);
                    }
                }
                else if (config.recursive) {
                    controlsConfig[key] = modelToFormGroup(model[key], config.validators);
                }
                else {
                    controlsConfig[key] = [model[key], config.validators];
                }
            }
            else {
                controlsConfig[key] = [model[key], config.validators];
            }
        }
    }
    return formBuilder.group(controlsConfig, options);
}

/*
 * Public API Surface of aautil
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AautilModule, CheckboxGroupModel, InFormGroup, SerializationOptions, SerializationService, Serialize, getValidators, modelToFormGroup };
